import { useMemo, useState } from "react"
import nProgress from "nprogress"
import toast from "react-hot-toast"

import SelectDescription from "components/forms/select-description"
import useBranchRepositories from "repositories/branch"
import { importLegacyYearlySales } from "repositories/legacy-sales"

const formatNumber = (n) => new Intl.NumberFormat("id-ID").format(Number(n || 0))

const downloadCsv = (filename, rows) => {
  // rows: array of arrays (first row = header). Each cell di-escape sesuai RFC 4180.
  const escape = (v) => {
    const s = String(v ?? "")
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const csv = rows.map((row) => row.map(escape).join(",")).join("\r\n")
  // BOM biar Excel ID baca UTF-8 dengan benar.
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

const PreviewSummary = ({ data }) => {
  if (!data) return null

  const monthBuckets = data.month_buckets_summary || {}
  const monthYear = data.month_year_map || {}
  const monthIdxToYearMonth = Object.entries(monthYear).map(([idx, ym]) => ({
    idx: Number(idx),
    ym,
    items: monthBuckets[ym] || 0,
  }))

  return (
    <div className="mt-6 space-y-4 text-xs">
      <div className="p-4 border rounded-xl border-neutral-200 bg-neutral-50">
        <div className="font-semibold mb-2">Ringkasan {data.dry_run ? "(Dry Run)" : ""}</div>
        <div className="grid grid-cols-2 gap-2">
          <div>As of date</div><div className="font-medium">{data.as_of_date}</div>
          <div>Total baris dibaca</div><div className="font-medium">{formatNumber(data.total_rows_parsed)}</div>
          <div>Baris produk match</div><div className="font-medium">{formatNumber(data.matched_product_rows)}</div>
          <div>Kode tidak ketemu</div><div className="font-medium text-red-700">{formatNumber(data.unmatched_count)}</div>
          <div>Cell qty=0 di-skip</div><div className="font-medium">{formatNumber(data.skipped_zero_qty)}</div>
          <div>{data.dry_run ? "Akan revive Product Unit" : "Product Unit ter-revive"}</div>
          <div className="font-medium text-amber-700">
            {formatNumber(data.dry_run ? data.will_revive_product_units : data.revived_product_units)}
          </div>
          <div>{data.dry_run ? "Akan revive Product Model" : "Product Model ter-revive"}</div>
          <div className="font-medium text-amber-700">
            {formatNumber(data.dry_run ? data.will_revive_product_models : data.revived_product_models)}
          </div>
          {!data.dry_run && (
            <>
              <div>Order ter-insert</div><div className="font-medium text-green-700">{formatNumber(data.inserted_orders)}</div>
              <div>Item ter-insert</div><div className="font-medium text-green-700">{formatNumber(data.inserted_items)}</div>
            </>
          )}
        </div>
      </div>

      <div className="p-4 border rounded-xl border-neutral-200">
        <div className="font-semibold mb-2">Mapping bulan → tahun-bulan</div>
        <div className="grid grid-cols-4 gap-1">
          {monthIdxToYearMonth.map(({ idx, ym, items }) => (
            <div key={idx} className="flex justify-between px-2 py-1 bg-neutral-50 rounded">
              <span className="text-neutral-600">{["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"][idx-1]}</span>
              <span className="font-medium">{ym} <span className="text-neutral-500">({items})</span></span>
            </div>
          ))}
        </div>
      </div>

      {data.unmatched_codes && data.unmatched_codes.length > 0 && (
        <div className="p-4 border rounded-xl border-amber-300 bg-amber-50">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="font-semibold text-amber-900">
              Kode barang tidak ditemukan di Legacy Mapping ({data.unmatched_codes.length})
            </div>
            <button
              type="button"
              onClick={() =>
                downloadCsv(
                  `unmatched-codes-${data.outlet_id}-${data.as_of_date}.csv`,
                  [
                    ["legacy_product_name"],
                    ...data.unmatched_codes.map((code) => [code]),
                  ]
                )
              }
              className="shrink-0 px-3 py-1.5 text-[11px] font-medium text-amber-900 bg-white border border-amber-300 rounded-lg hover:bg-amber-100 transition"
            >
              Download CSV
            </button>
          </div>
          <div className="max-h-48 overflow-auto pl-4">
            <ul className="list-disc space-y-1 text-amber-900">
              {data.unmatched_codes.slice(0, 200).map((code, idx) => (
                <li key={idx}>{code}</li>
              ))}
              {data.unmatched_codes.length > 200 && (
                <li className="italic">... dan {data.unmatched_codes.length - 200} kode lainnya</li>
              )}
            </ul>
          </div>
          <div className="mt-2 text-[11px] text-amber-800">
            Tambahkan mapping legacy → product unit terlebih dahulu di halaman Import Legacy Mapping
            agar kode-kode ini ikut ter-import. Download CSV di atas bisa langsung dipakai sebagai
            template (tambahkan kolom <code>product_unit_id</code>) untuk Import Legacy Mapping.
          </div>
        </div>
      )}

      {data.deleted_but_imported_codes && data.deleted_but_imported_codes.length > 0 && (
        <div className="p-4 border rounded-xl border-blue-300 bg-blue-50">
          <div className="font-semibold mb-2 text-blue-900">
            Product unit sudah deleted di master, tetap di-import ({data.deleted_but_imported_codes.length})
          </div>
          <div className="max-h-32 overflow-auto pl-4">
            <ul className="list-disc space-y-1 text-blue-900">
              {data.deleted_but_imported_codes.map((code, idx) => (
                <li key={idx}>{code}</li>
              ))}
            </ul>
          </div>
          <div className="mt-2 text-[11px] text-blue-800">
            Order item tetap ter-insert dengan product_unit_id dari mapping. Nama produk pakai kode CSV
            sebagai fallback karena master sudah hilang.
          </div>
        </div>
      )}
    </div>
  )
}

const ImportLegacySalesPage = () => {
  const [file, setFile] = useState(null)
  const [outletId, setOutletId] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState(null)

  const { data: outletSelection, isLoading: isLoadingOutletSelection } =
    useBranchRepositories({ paginate: false })

  const outletOptions = useMemo(() => outletSelection || [], [outletSelection])

  const run = async (dryRun) => {
    if (!outletId) {
      toast.error("Silakan pilih outlet terlebih dahulu")
      return
    }
    if (!file) {
      toast.error("Silakan pilih file CSV terlebih dahulu")
      return
    }

    nProgress.start()
    setSubmitting(true)
    setResult(null)

    try {
      const data = await importLegacyYearlySales({
        outletId,
        file,
        dryRun,
      })
      setResult(data?.data || null)
      toast.success(data?.message || (dryRun ? "Dry run selesai" : "Import selesai"))
    } catch (error) {
      const status = error?.response?.status
      const msg = error?.response?.data?.message || "Gagal memproses file"
      toast.error(msg)
      if (status === 422) {
        const errs = error.response.data?.errors
        if (errs && typeof errs === "object") {
          Object.values(errs).flat().forEach((e) => toast.error(e))
        }
      }
    } finally {
      nProgress.done()
      setSubmitting(false)
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <div className="space-y-1 mb-6">
        <h1 className="text-lg font-semibold text-neutral-900">
          Migrasi Penjualan Setahun (Legacy)
        </h1>
        <p className="text-xs text-neutral-600">
          Import data penjualan setahun dari sistem lama (file CSV "informasi penjualan barang")
          ke dalam <code className="px-1 bg-neutral-100 rounded">orders</code> +
          {" "}<code className="px-1 bg-neutral-100 rounded">order_items</code>.
          Setelah import, data akan muncul di Laporan Qty Penjualan Tahunan.
        </p>
      </div>

      <div className="space-y-2 mb-4 p-3 bg-blue-50 border border-blue-200 rounded-xl text-[11px] text-blue-900">
        <div className="font-semibold">Catatan penting</div>
        <ul className="pl-5 list-disc space-y-1">
          <li>
            Hanya backfill <strong>qty</strong> — harga di-set 0 (file CSV tidak punya price).
            Laporan Amount Yearly tidak akan ter-backfill.
          </li>
          <li>
            Kolom JAN-DEC dipetakan rolling 12 bulan dari hari ini. Mis. hari ini Mei → JAN..MAY = 2026,
            JUN..DEC = 2025.
          </li>
          <li>
            Match KODE BARANG via Legacy Product Unit Mapping. Yang belum ada mapping → di-skip.
            Tambahkan mapping dulu lewat halaman Import Legacy Mapping.
          </li>
          <li>
            Product Unit / Product Model yang sudah soft-deleted di master tapi jadi target
            mapping legacy akan <strong>di-revive otomatis</strong> (un-delete). Tanpa ini,
            order ter-insert tapi tidak muncul di Laporan Qty Tahunan karena report filter
            out PU/PM yang soft-deleted.
          </li>
          <li>
            <strong>Gunakan Preview (Dry Run)</strong> dulu untuk lihat berapa yang match
            sebelum eksekusi Import.
          </li>
          <li>
            Re-import outlet yang sama akan ditolak (cek prefix <code>OLG-{"{outlet}"}</code>).
            Hapus order legacy lama dulu kalau mau ulang.
          </li>
        </ul>
      </div>

      <div className="space-y-4 text-xs">
        <div className="space-y-2">
          <label className="block text-xs font-medium text-neutral-700">Outlet</label>
          <SelectDescription
            onChange={(value) => {
              setOutletId(value)
              setResult(null)
            }}
            selection={outletOptions}
            isLoading={isLoadingOutletSelection}
            value={outletId}
            keyValue={(value) => value.id}
            title={(value) => value.name}
            description={(value) => value.code || value.id}
            placeholder="Pilih Outlet"
          />
          {outletId && (
            <p className="text-[11px] text-neutral-500">
              Outlet:{" "}
              <span className="font-medium">
                {outletOptions.find((o) => o.id === outletId)?.name || outletId}
              </span>
            </p>
          )}
        </div>

        <div className="space-y-2">
          <label className="block text-xs font-medium text-neutral-700">File CSV</label>
          <input
            type="file"
            accept=".csv,.txt"
            onChange={(e) => {
              const f = e.target.files?.[0]
              setFile(f || null)
              setResult(null)
            }}
            className="block w-full text-xs text-neutral-700 border border-neutral-200 rounded-xl cursor-pointer file:mr-3 file:px-3 file:py-2 file:text-xs file:font-medium file:border-0 file:rounded-xl file:bg-brand-800 file:text-white hover:file:bg-brand-900"
          />
          {file && (
            <p className="text-[11px] text-neutral-500">
              Selected file: <span className="font-medium">{file.name}</span>
            </p>
          )}
        </div>

        <div className="flex items-center space-x-3 pt-2">
          <button
            type="button"
            onClick={() => run(true)}
            disabled={submitting}
            className="inline-flex items-center px-5 py-3 text-white transition bg-neutral-500 rounded-xl active:hover:scale-90 disabled:opacity-60"
          >
            {submitting ? "Memproses..." : "Preview (Dry Run)"}
          </button>
          <button
            type="button"
            onClick={() => {
              if (!window.confirm("Eksekusi import? Data akan masuk ke tabel orders & order_items.")) return
              run(false)
            }}
            disabled={submitting}
            className="inline-flex items-center px-5 py-3 text-white transition bg-brand-800 rounded-xl active:hover:scale-90 disabled:opacity-60"
          >
            {submitting ? "Memproses..." : "Import"}
          </button>
        </div>

        <PreviewSummary data={result} />
      </div>
    </div>
  )
}

export default ImportLegacySalesPage
